import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  InputAdornment,
  Grid,
  Checkbox,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  CloudUpload as UploadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { activityAPI } from '../services/api';
import { toast } from 'react-toastify';

const ACTION_TYPES = [
  { value: '', label: 'All Actions' },
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'approve', label: 'Approve' },
  { value: 'reject', label: 'Reject' },
  { value: 'upload', label: 'Upload' },
];

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  
  return date.toLocaleDateString();
};

const formatIPAddress = (ip) => {
  if (!ip) return '—';
  if (ip === '::1' || ip === '127.0.0.1') return 'Localhost';
  return ip;
};

const getDisplayName = (performedBy) => {
  if (!performedBy) return 'System';
  if (performedBy.type === 'admin') {
    return performedBy.adminId?.username || 'Admin';
  }
  return performedBy.userId?.fullName || performedBy.userId?.email || 'System';
};

const getDisplayEmail = (performedBy) => {
  if (!performedBy) return '';
  if (performedBy.type === 'admin') {
    return performedBy.adminId?.email || '';
  }
  return performedBy.userId?.email || '';
};

const getActionIcon = (actionType) => {
  switch (actionType) {
    case 'login':
      return <LoginIcon sx={{ fontSize: 16 }} />;
    case 'logout':
      return <LogoutIcon sx={{ fontSize: 16 }} />;
    case 'create':
      return <AddIcon sx={{ fontSize: 16 }} />;
    case 'update':
      return <EditIcon sx={{ fontSize: 16 }} />;
    case 'delete':
      return <DeleteIcon sx={{ fontSize: 16 }} />;
    case 'approve':
      return <ApproveIcon sx={{ fontSize: 16 }} />;
    case 'reject':
      return <RejectIcon sx={{ fontSize: 16 }} />;
    case 'upload':
      return <UploadIcon sx={{ fontSize: 16 }} />;
    default:
      return null;
  }
};

const getActionColor = (actionType) => {
  switch (actionType) {
    case 'login':
      return 'info';
    case 'logout':
      return 'default';
    case 'create':
    case 'upload':
      return 'success';
    case 'update':
      return 'primary';
    case 'delete':
      return 'error';
    case 'approve':
      return 'success';
    case 'reject':
      return 'warning';
    default:
      return 'default';
  }
};

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalLogs, setTotalLogs] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    actionType: '',
    startDate: '',
    endDate: '',
  });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, [page, rowsPerPage]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await activityAPI.getActivityLogs({
        page: page + 1,
        limit: rowsPerPage,
        action: filters.actionType,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
      setLogs(response.data.data?.activities || []);
      setTotalLogs(response.data.data?.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchLogs();
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      actionType: '',
      startDate: '',
      endDate: '',
    });
    setPage(0);
    setTimeout(fetchLogs, 100);
  };

  const handleExport = async () => {
    try {
      const response = await activityAPI.exportLogs(filters);
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Activity logs exported successfully');
    } catch (error) {
      console.error('Error exporting logs:', error);
      toast.error('Failed to export activity logs');
    }
  };

  const handleSelectRow = (logId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(logId)) {
      newSelected.delete(logId);
    } else {
      newSelected.add(logId);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAllRows = () => {
    if (selectedRows.size === logs.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(logs.map(log => log._id)));
    }
  };

  const handleExportSelected = async () => {
    if (selectedRows.size === 0) {
      toast.info('Please select rows to export');
      return;
    }

    try {
      const selectedLogs = logs.filter(log => selectedRows.has(log._id));
      const csv = convertLogsToCSV(selectedLogs);
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-logs-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${selectedRows.size} log entries`);
      setSelectedRows(new Set());
    } catch (error) {
      console.error('Error exporting selected logs:', error);
      toast.error('Failed to export selected logs');
    }
  };

  const convertLogsToCSV = (logsToExport) => {
    const headers = ['Timestamp', 'User', 'Email', 'Action', 'Details', 'IP Address'];
    const rows = logsToExport.map(log => [
      new Date(log.timestamp).toLocaleString(),
      getDisplayName(log.performedBy),
      getDisplayEmail(log.performedBy),
      log.actionType || log.action,
      typeof log.details === 'string' ? log.details : JSON.stringify(log.details),
      formatIPAddress(log.ipAddress),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    return csvContent;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Activity Logs</Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {selectedRows.size > 0 && (
            <Tooltip title={`Export ${selectedRows.size} selected entries`}>
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportSelected}
              >
                Export Selected ({selectedRows.size})
              </Button>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchLogs}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export All
          </Button>
        </Box>
      </Box>

      {selectedRows.size > 0 && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          onClose={() => setSelectedRows(new Set())}
        >
          {selectedRows.size} row(s) selected
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ mb: 3, border: '1px solid', borderColor: '#e5e7eb' }}>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center', 
            p: 1.5,
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#f9fafb' }
          }}
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon sx={{ fontSize: 20, color: '#6b7280' }} />
            <Typography variant="subtitle2" sx={{ color: '#374151', fontWeight: 500 }}>
              Filters
            </Typography>
            {Object.values(filters).some(v => v !== '') && (
              <Chip 
                label={Object.values(filters).filter(v => v !== '').length}
                size="small"
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
            )}
          </Box>
          <Box>
            {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        </Box>

        {filtersExpanded && (
          <Box sx={{ p: 2, pt: 1, borderTop: '1px solid #e5e7eb' }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="Search logs..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Action Type"
                  value={filters.actionType}
                  onChange={(e) => handleFilterChange('actionType', e.target.value)}
                >
                  {ACTION_TYPES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End Date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    onClick={handleApplyFilters}
                  >
                    Apply
                  </Button>
                  <Button
                    fullWidth
                    variant="text"
                    size="small"
                    onClick={handleResetFilters}
                    sx={{ color: '#6b7280' }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Activity Logs Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedRows.size > 0 && selectedRows.size < logs.length}
                  checked={logs.length > 0 && selectedRows.size === logs.length}
                  onChange={handleSelectAllRows}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">No activity logs found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow 
                  key={log._id} 
                  hover
                  sx={{
                    backgroundColor: selectedRows.has(log._id) ? '#f0f9ff' : 'inherit',
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedRows.has(log._id)}
                      onChange={() => handleSelectRow(log._id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={new Date(log.timestamp).toLocaleString()}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {formatRelativeTime(log.timestamp)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1f2937' }}>
                        {getDisplayName(log.performedBy)}
                      </Typography>
                      {getDisplayEmail(log.performedBy) && (
                        <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                          {getDisplayEmail(log.performedBy)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getActionIcon(log.actionType || log.action)}
                      label={log.actionType || log.action}
                      color={getActionColor(log.actionType || log.action)}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        fontWeight: 500,
                        '& .MuiChip-icon': {
                          marginLeft: '4px !important'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 250,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: '#6b7280',
                      }}
                      title={typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}
                    >
                      {typeof log.details === 'string' ? log.details : JSON.stringify(log.details) || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={log.ipAddress || 'N/A'}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#6b7280' }}>
                        {formatIPAddress(log.ipAddress)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalLogs}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Summary Stats */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Total Logs
              </Typography>
              <Typography variant="h5">{totalLogs}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Current Page
              </Typography>
              <Typography variant="h5">
                {page + 1} / {Math.ceil(totalLogs / rowsPerPage) || 1}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Showing
              </Typography>
              <Typography variant="h5">
                {Math.min(page * rowsPerPage + 1, totalLogs)} -{' '}
                {Math.min((page + 1) * rowsPerPage, totalLogs)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Filters Applied
              </Typography>
              <Typography variant="h5">
                {Object.values(filters).filter((v) => v !== '').length}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ActivityLogs;